import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import SpellcheckRoundedIcon from '@mui/icons-material/SpellcheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import PageTitle from '../components/PageTitle';
import TopWordsWidget from '../components/TopWordsWidget';
import TopLongestWordsWidget from '../components/TopLongestWordsWidget';
import ScoreTrendWidget from '../components/widgets/ScoreTrendWidget';
import PersonalRecordsWidget from '../components/widgets/PersonalRecordsWidget';
import EconomyWidget from '../components/widgets/EconomyWidget';
import WordcraftWidget from '../components/widgets/WordcraftWidget';
import BagTasteWidget from '../components/widgets/BagTasteWidget';
import RunsWidget from '../components/widgets/RunsWidget';
import LetterFrequencyWidget from '../components/widgets/LetterFrequencyWidget';
import ShopTendenciesWidget from '../components/widgets/ShopTendenciesWidget';
import {
  getStoredPlayerStats,
  resetStoredPlayerStats,
  subscribeToPlayerStatsChanges,
} from '../features/scrabble/services/localStorageService';
import { toNumber, formatInt } from '../utils/format';

const getPlayerStatsSnapshot = () => getStoredPlayerStats();

function MetricTile({ icon, label, value, helper }) {
  return (
    <Card
      sx={{
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
        boxShadow: (theme) => `0 10px 26px ${alpha(theme.palette.primary.main, 0.08)}`,
        borderRadius: 3,
        height: '100%',
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}
          >
            {icon}
          </Avatar>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StatsPage() {
  const [stats, setStats] = React.useState(() => getPlayerStatsSnapshot());
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [resetConfirmed, setResetConfirmed] = React.useState(false);

  React.useEffect(() => {
    const syncStats = () => {
      setStats(getPlayerStatsSnapshot());
    };

    syncStats();

    return subscribeToPlayerStatsChanges(syncStats);
  }, []);

  const topWordPlays = Array.isArray(stats.history?.topWordPlays)
    ? stats.history.topWordPlays.slice(0, 5)
    : [];
  const topLongestWordPlays = Array.isArray(stats.history?.topLongestWordPlays)
    ? stats.history.topLongestWordPlays.slice(0, 5)
    : [];

  const handleResetOpen = () => {
    setResetConfirmed(false);
    setResetDialogOpen(true);
  };

  const handleResetClose = () => {
    setResetDialogOpen(false);
    setResetConfirmed(false);
  };

  const handleResetConfirm = () => {
    resetStoredPlayerStats();
    setResetDialogOpen(false);
    setResetConfirmed(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 10% -10%, rgba(25, 118, 210, 0.14), transparent 45%), linear-gradient(180deg, #f8fbff 0%, #f2f6fd 100%)',
      }}
    >
      <Stack spacing={2.5} sx={{ maxWidth: 1120, mx: 'auto' }}>
        <PageTitle subtitle="Player Stats" />

        <Grid container spacing={1.5} alignItems="stretch">
          <Grid size={{ xs: 12, md: 7 }}>
            <ScoreTrendWidget stats={stats} />
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <PersonalRecordsWidget stats={stats} />
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <MetricTile
              icon={<AutoGraphRoundedIcon fontSize="small" />}
              label="Lifetime Points"
              value={formatInt(stats.scoring?.lifetimePoints)}
              helper="Total score banked"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <MetricTile
              icon={<SpellcheckRoundedIcon fontSize="small" />}
              label="Avg Word Score"
              value={toNumber(stats.scoring?.averageWordScore).toFixed(1)}
              helper="Across all submitted words"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <MetricTile
              icon={<CheckCircleRoundedIcon fontSize="small" />}
              label="Avg Submit Score"
              value={toNumber(stats.scoring?.averageSubmitScore).toFixed(1)}
              helper="Per submit action"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <MetricTile
              icon={<DeleteSweepRoundedIcon fontSize="small" />}
              label="Total Discards"
              value={formatInt(stats.scoring?.lifetimeDiscardsUsed)}
              helper="Discard actions spent"
            />
          </Grid>
        </Grid>

        <Grid container spacing={1.5} alignItems="stretch">
          <Grid size={{ xs: 12, md: 6 }}>
            <TopWordsWidget topWordPlays={topWordPlays} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TopLongestWordsWidget topLongestWordPlays={topLongestWordPlays} />
          </Grid>
        </Grid>

        <Grid container spacing={1.5} alignItems="stretch">
          <Grid size={{ xs: 12, md: 6 }}>
            <EconomyWidget stats={stats} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <WordcraftWidget stats={stats} />
          </Grid>
        </Grid>

        <Grid container spacing={1.5} alignItems="stretch">
          <Grid size={{ xs: 12, md: 5 }}>
            <BagTasteWidget stats={stats} />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <RunsWidget stats={stats} />
          </Grid>
        </Grid>

        <Grid container spacing={1.5} alignItems="stretch">
          <Grid size={{ xs: 12, md: 7 }}>
            <LetterFrequencyWidget stats={stats} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <ShopTendenciesWidget stats={stats} />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForeverRoundedIcon />}
            onClick={handleResetOpen}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Reset my stats
          </Button>
        </Box>
      </Stack>

      <Dialog open={resetDialogOpen} onClose={handleResetClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <WarningAmberRoundedIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Reset all player stats?
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="error" icon={false}>
              This will permanently delete all of your tracked statistics and run history, and cancel
              any game in progress. This cannot be undone.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              All tracked stats, run history, and any active game in progress will be wiped. Your
              account will be returned to a clean slate.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={resetConfirmed}
                  onChange={(e) => setResetConfirmed(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  I understand this cannot be undone
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleResetClose} sx={{ fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!resetConfirmed}
            onClick={handleResetConfirm}
            startIcon={<DeleteForeverRoundedIcon />}
            sx={{ fontWeight: 700 }}
          >
            Reset Stats
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StatsPage;
